import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import type { OutboundCallerId } from "services/dialstack";
import { formatPhoneNumber } from "utils/common";

import { lastTenDigits } from "../phone-match.util";
import {
  callerIdFieldSx,
  callerIdOptionNumberSx,
  callerIdOptionSx,
  callerIdRowSx,
  e911SelectMenuSx
} from "./dialstack-softphone-widget.styles";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";

export const CALLER_ID_LABEL = "Call from";
export const CALLER_ID_DEFAULT_SUFFIX = " (default)";
// When the platform's own default cannot be told, the picker sits on this
// until the user chooses; a dial then presents whatever the platform picks.
export const CALLER_ID_AUTOMATIC_LABEL = "Default number";

// Numbers arrive in E.164; the formatter wants the national ten digits.
export const formatCallerIdNumber = (phoneNumber: string) =>
  formatPhoneNumber(lastTenDigits(phoneNumber)) || phoneNumber;

export function callerIdLabel(number: OutboundCallerId) {
  return (
    number.clinic_name ||
    number.caller_id_name ||
    formatCallerIdNumber(number.phone_number)
  );
}

// Which of the practice's numbers the next dial from the widget presents
// (ENG-1938). Shown only when there is a choice to make. An explicit pick
// applies to one call and then clears; with none, a dial to a patient uses
// their clinic's number and any other dial the standing one.
export default function CallerIdSelect() {
  const { callerIds, standingCallerId, selectedCallerId, setSelectedCallerId } =
    useDialstackSoftphone();
  if (callerIds.length < 2) return null;

  const value = selectedCallerId ?? standingCallerId ?? "";
  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedCallerId(event.target.value || null);
  };

  // The card carries the margins; a `fullWidth` control with margins of its
  // own would be 100% + 20px and make the panel scroll sideways.
  return (
    <Box sx={callerIdRowSx}>
      <FormControl fullWidth size="small" sx={callerIdFieldSx}>
        <InputLabel id="suno-caller-id-label">{CALLER_ID_LABEL}</InputLabel>
        <Select
          labelId="suno-caller-id-label"
          label={CALLER_ID_LABEL}
          value={value}
          displayEmpty={value === ""}
          renderValue={(selected) => {
            const number = callerIds.find((entry) => entry.id === selected);
            if (!number) return CALLER_ID_AUTOMATIC_LABEL;
            return `${callerIdLabel(number)} · ${formatCallerIdNumber(
              number.phone_number
            )}`;
          }}
          onChange={handleChange}
          MenuProps={{ sx: e911SelectMenuSx }}
          inputProps={{ "aria-label": CALLER_ID_LABEL }}
        >
          {callerIds.map((number) => (
            <MenuItem key={number.id} value={number.id} sx={callerIdOptionSx}>
              <span>
                {callerIdLabel(number)}
                {number.id === standingCallerId ? CALLER_ID_DEFAULT_SUFFIX : ""}
              </span>
              <Typography component="span" sx={callerIdOptionNumberSx}>
                {formatCallerIdNumber(number.phone_number)}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
